import { Route, RouteConfig } from "vue-router";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import { getLoginData } from "@/lib/authHelper";
import { authoritiesRouteGuard } from "@/lib/routerGuards";
import { getProfileUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { enrichRouteMeta } from "@/router/helpers";
import { FollowersPageTabs } from "@/views/PageFollowers/types";
import PageForgotPasswordConfirmCode from "@/views/PageForgotPasswordConfirmCode";
import PageForgotPasswordSet from "@/views/PageForgotPasswordSet";

const routes: RouteConfig[] = [
  ...(config.soloCreatorId === null
    ? [
        {
          path: "/",
          name: "index",
          component: () => import("@/views/PageLanding"),
          props: (route: Route) => ({
            category: route.query.category,
            screenName: route.query.screenName,
          }),
        },
      ]
    : []),
  {
    path: "/home",
    name: "home",
    components: {
      default: () => import("@/views/PageFeed"),
      unlockFeed: () => import(/* webpackChunkName: "profile" */ "@/views/UnlockFeed"),
    },
    ...(config.soloCreatorId !== null ? { redirect: { name: "profileBySoloCreatorId" } } : {}),
  },
  {
    path: "/messages",
    name: "messageThreads",
    component: () => import("@/views/PageMessageThreads"),
    props: route => ({ isMassMessage: !!route.query.massMessage }),
  },
  {
    path: "/messages/mailing",
    name: "massMessage",
    component: () => import("@/views/PageMassMessage"),
    props: route => ({ ids: route.params.ids ? route.params.ids.split(",") : undefined }),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["updateMyContentPrice"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/messages/:userId",
    name: "messages",
    component: () => import("@/views/PageMessages"),
    props: true,
  },
  {
    path: "/messages/threads/:threadId",
    name: "generalMessageThread",
    component: () => import("@/views/PageMessages"),
    props: true,
  },
  {
    path: "/notifications",
    name: "notifications",
    component: () => import("@/views/PageNotifications"),
    props: true,
  },
  {
    path: "/more",
    name: "more",
    props: route => ({ section: route.query.section }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageMore"),
  },
  {
    path: "/more/payeeInfo",
    name: "profilePayeeInfo",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayeeInfo"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (config.soloCreatorId !== null && config.soloCreatorId !== data?.user.id) {
        next("/");
        return;
      }
      next();
    },
  },
  {
    path: "/more/payeeInfoEdit",
    name: "profilePayeeInfoEdit",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayeeInfoEdit"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (config.soloCreatorId !== null && config.soloCreatorId !== data?.user.id) {
        next("/");
        return;
      }
      next();
    },
  },
  {
    path: "/more/sublevels",
    name: "profileSubscriptionLevels",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageSubscriptionLevels"),
  },
  {
    path: "/more/welcomeMessage",
    name: "welcomeMessage",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageWelcomeMessage"),
  },
  {
    path: "/more/cards",
    name: "profilePaymentCards",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePaymentCards"),
  },
  {
    path: "/more/balance",
    name: "balance",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageBalance"),
  },
  ...(config.featurePayoutRequest
    ? [
        {
          path: "/more/payout-requests",
          name: "payoutRequests",
          props: true,
          component: () => import(/* webpackChunkName: "more" */ "@/views/PagePayoutRequests"),
        },
      ]
    : []),
  {
    path: "/more/massMessagesStatistics",
    name: "massMessagesStatistics",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageMassMessageStat"),
  },
  {
    path: "/more/lists",
    name: "manageGroups",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageGroups"),
  },
  {
    path: "/more/lists/:groupId",
    name: "manageGroupUsers",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageGroupUsers"),
  },
  ...(config.soloCreatorId === null
    ? [
        {
          path: "/more/referrals",
          name: "referrals",
          props: true,
          component: () => import(/* webpackChunkName: "more" */ "@/views/PageReferrals"),
        },
      ]
    : []),
  {
    path: "/more/priceSettings",
    name: "priceSettings",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePriceSettings"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["updateMyMessagePrice"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/incomingCallsSettings",
    name: "incomingCallsSettings",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageIncomingCallsPriceSettings"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["updateMyCallOptions", "updateMyCallPrice"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/autoPosting",
    name: "autoPosting",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageAutoPosting"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["updateMyAutomatedPost"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/autoMassMessage",
    name: "autoMassMessage",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageAutoMassMessage"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["updateMyAutomatedMassMessage"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/recurring",
    name: "manageRecurring",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageManageRecurring"),
  },
  {
    path: "/more/vault",
    name: "manageVault",
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageVault"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["updateMyContentPrice"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/emailNotificationsSettings",
    name: "emailNotificationsSettings",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageEmailNotificationsSettings"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (!data?.user.email) {
        next({ name: "more" });
        return;
      }
      next();
    },
  },
  ...(config.webPushNotificationsPublicKey
    ? [
        {
          path: "/more/pushNotificationsSettings",
          name: "pushNotificationsSettings",
          props: true,
          component: () => import(/* webpackChunkName: "more" */ "@/views/PagePushNotificationsSettings"),
        },
      ]
    : []),
  {
    path: "/more/account/change-password",
    name: "changePassword",
    component: PageForgotPasswordConfirmCode,
    props: () => {
      const data = getLoginData();
      return {
        email: data?.user.email,
        changePassword: true,
      };
    },
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (!data?.user.email) {
        next({ name: "more" });
        return;
      }
      next();
    },
  },
  {
    path: "/more/account/change-password/:token",
    name: "changePasswordSet",
    component: PageForgotPasswordSet,
    props: route => {
      const data = getLoginData();
      return {
        email: data?.user.email,
        token: route.params.token,
        changePassword: true,
      };
    },
  },
  {
    path: "/profile",
    name: "myProfile",
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (data) next(getProfileUrl(data.user));
      else next("/");
    },
  },
  {
    path: "/profile/:userId/edit",
    name: "profilePageEdit",
    props: true,
    component: () => import(/* webpackChunkName: "profile" */ "@/views/PageProfileEdit"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const isAuthor = data?.user.id === to.params.userId;
      const requiredAuthorities = isAuthor
        ? ["updateMyQuestionaryInfo", "updateMyPersonalInfo", "updateMyFile"]
        : ["updateAnyQuestionaryInfo", "updateAnyPersonalInfo", "updateAnyFile"];
      if (checkAuthorities(data?.authorities ?? [], requiredAuthorities)) next();
      else next(getProfileUrl(to.params.userId));
    },
  },
  {
    path: "/profile/:userId/subscribers",
    name: "subscribersById",
    component: () => import("@/views/PageFollowers"),
    props: (route: Route) => ({
      userId: route.params.userId,
      page: FollowersPageTabs.SUBSCRIBERS,
    }),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const isAuthor = data?.user.id === to.params.userId;
      isAuthor ? next() : next({ name: "/" });
    },
  },
  {
    path: "/profile/:userId/followers",
    name: "followersById",
    component: () => import("@/views/PageFollowers"),
    props: (route: Route) => ({
      userId: route.params.userId,
      page: FollowersPageTabs.FOLLOWERS,
    }),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const isAuthor = data?.user.id === to.params.userId;
      isAuthor ? next() : next({ name: "/" });
    },
  },
  {
    path: "/profile/:userId/following",
    name: "followingById",
    component: () => import("@/views/PageFollowers"),
    props: (route: Route) => ({
      userId: route.params.userId,
      page: FollowersPageTabs.FOLLOWING,
    }),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const isAuthor = data?.user.id === to.params.userId;
      isAuthor ? next() : next({ name: "/" });
    },
  },
  {
    path: "/ai-questionnaire/:userId",
    name: "aiQuestionary",
    props: (route: Route) => ({
      userId: route.params.userId,
    }),
    component: () => import("@/views/PageAiQuestionary"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      const isAuthor = data?.user.id === to.params.userId;
      authoritiesRouteGuard([isAuthor ? "getMyAiUserQuestionary" : "getAnyAiUserQuestionary"])
        ? next()
        : next({ name: "/" });
    },
  },
  {
    path: "/link-analytics",
    name: "linkAnalytics",
    props: true,
    component: () => import(/* webpackChunkName: "linkAnalytics" */ "@/views/PageLinkAnalytics"),
  },
  {
    path: "/profile-analytics",
    name: "profileAnalytics",
    props: true,
    component: () => import(/* webpackChunkName: "linkAnalytics" */ "@/views/PageProfileAnalytics"),
    beforeEnter(to, from, next) {
      const data = getLoginData();
      if (!checkRole(data?.user.role || "", "creator")) {
        next({ name: "myProfile" });
        return;
      }
      next();
    },
  },
  {
    path: "/more/purchases",
    name: "purchases",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePurchaseGallery"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyPurchasedFile"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/purchases/:username",
    name: "purchasesFeed",
    props: (route: Route) => ({
      onFeed: false,
      username: route.params.username,
    }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePurchasesGalleryFeed"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyPurchasedFile"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/purchases/:username/feed",
    name: "purchasesFeedList",
    props: (route: Route) => ({
      onFeed: true,
      username: route.params.username,
    }),
    component: () => import(/* webpackChunkName: "more" */ "@/views/PagePurchasesGalleryFeed"),
    beforeEnter(_, __, next) {
      authoritiesRouteGuard(["getMyPurchasedFile"]) ? next() : next({ name: "/" });
    },
  },
  {
    path: "/more/statements",
    name: "statements",
    props: true,
    component: () => import(/* webpackChunkName: "more" */ "@/views/PageStatements"),
  },
];

export default routes.map(route => enrichRouteMeta(route, { private: true }));
